import React, { useMemo } from "react";

import { faStopwatch } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ColumnType } from "antd/es/table";
import { defineMessages, useIntl } from "react-intl";

import { Color } from "@mapmycustomers/shared";
import { EntityTypeSupportingGroups, Group } from "@mapmycustomers/shared/types/entity";
import { UserRef } from "@mapmycustomers/shared/types/User";
import anyColorToHex from "@mapmycustomers/shared/util/color/anyColorToHex";
import { nameComparator, userDisplayNameComparator } from "@mapmycustomers/shared/util/comparator";
import { AvatarWithName } from "@mapmycustomers/ui";

import styles from "./useColumns.module.scss";

const messages = defineMessages({
  cadence: {
    id: "map.toolbar.groups.modal.item.cadence",
    defaultMessage: "{icon} {days} {days, plural, one {day} other {days}}",
    description: "Cadence tag in Group item in Groups popover on the Map page",
  },
  count: {
    id: "map.toolbar.groups.modal.item.count",
    defaultMessage:
      "{count, plural, =0 {{entityType, select, accounts {No Companies} contacts {No People} deals {No Deals} other {No records}}} one {{count} {entityType, select, accounts {Company} contacts {Person} deals {Deal} other {record}}} other {{count} {entityType, select, accounts {Companies} contacts {People} deals {Deals} other {records}}}}",
    description: "Records in group count information in Groups popover on the Map page",
  },
  creator: {
    id: "map.toolbar.groups.modal.column.creator",
    defaultMessage: "Creator",
    description: "Creator column title",
  },
  frequency: {
    id: "map.toolbar.groups.modal.column.frequency",
    defaultMessage: "Frequency",
    description: "Frequency column title",
  },
  groups: {
    id: "map.toolbar.groups.modal.column.title",
    defaultMessage:
      "{entityType, select, accounts {Company Group} contacts {People Group} deals {Deal Group} other {Group}}",
  },
});

const useColumns = (entityType: EntityTypeSupportingGroups) => {
  const intl = useIntl();
  return useMemo<ColumnType<Group>[]>(
    () => [
      {
        dataIndex: "name",
        render: (name: Group["name"], group) => (
          <div className={styles.container}>
            <div className={styles.name}>
              <div
                className={styles.dot}
                style={{
                  backgroundColor: anyColorToHex(group.color || Color.BLACK),
                }}
              />
              <span>{name}</span>
            </div>
            <div className={styles.count}>
              {intl.formatMessage(messages.count, { count: group.items, entityType })}
            </div>
          </div>
        ),
        sorter: nameComparator,
        title: intl.formatMessage(messages.groups, { entityType }),
        width: "45%",
      },
      {
        dataIndex: "user",
        render: (user: UserRef) => <AvatarWithName className={styles.avatar} user={user} />,
        sorter: (a, b) => userDisplayNameComparator(a.user, b.user),
        title: intl.formatMessage(messages.creator),
        width: "9rem",
      },
      {
        dataIndex: "cadenceInterval",
        render: (cadenceInterval: Group["cadenceInterval"]) =>
          cadenceInterval && (
            <div className={styles.cadence}>
              {intl.formatMessage(messages.cadence, {
                days: cadenceInterval,
                icon: <FontAwesomeIcon icon={faStopwatch} />,
              })}
            </div>
          ),
        sorter: (a, b) => (a.cadenceInterval ?? 0) - (b.cadenceInterval ?? 0),
        title: intl.formatMessage(messages.frequency),
        width: "7rem",
      },
    ],
    [entityType, intl]
  );
};

export default useColumns;
